import React, { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, CircularProgress, Divider, Grid, IconButton, TextField, Typography } from '@mui/material';

import { GridBreak } from 'components/GridBreak';
import FieldAutocompleteList from 'components/forms/fields/FieldAutocompleteList';
import { role } from './constants';
import { useUserSettingsStateContext } from 'core/user.settings/hooks';
import { useGetAgencies } from 'core/agencies/hooks';
import { Button } from 'components/buttons/button';
import { AddCircleOutlineOutlined, HighlightOff } from '@mui/icons-material';

const Form = () => {
    const {register, control, formState: {errors}} = useFormContext<any>();
    const {data: agenciesData, isLoading: isAgenciesLoading, isError: isAgenciesError} = useGetAgencies();
    const {fields, append, remove, } = useFieldArray({control: control, name: 'agency_ids', rules: {minLength: 0}});
    const {user: userData} = useUserSettingsStateContext();
    const user = JSON.parse(userData || '{}');

    const agencyOptions = useMemo(() => {
        return agenciesData?.reduce((a: any, v: any) => {a.push([v.id, v.closed ? `${v.name} (fermée)` : v.name]); return a;}, []) || [];
    }, [agenciesData]);

    return (
        <Box sx={{p: '1rem 1.5rem'}}>
            <Box>
                <Typography variant="h5">Informations personnelles</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1}>
                        <TextField
                            id='first_name'
                            label='Prénom'
                            {...register('first_name')}
                            autoComplete='off'
                            error={errors?.first_name ? true : false}
                            helperText={errors?.first_name ? errors.first_name.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='last_name'
                            label='Nom de famille'
                            {...register('last_name')}
                            autoComplete='off'
                            error={errors?.last_name ? true : false}
                            helperText={errors?.last_name ? errors.last_name.message as string : null}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Compte</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1}>
                        <TextField
                            id='email'
                            label='Email'
                            {...register('email')}
                            autoComplete='off'
                            error={errors?.email ? true : false}
                            helperText={errors?.email ? errors.email.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            id='password'
                            label='Mot de Passe'
                            type='password'
                            {...register('password')}
                            autoComplete='off'
                            error={errors?.password ? true : false}
                            helperText={errors?.password ? errors.password.message as string : null}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FieldAutocompleteList
                            control={control}
                            options={role(user.role)}
                            fieldName='role'
                            labelId='role'
                            label="Rôle"
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
                <Typography variant="h5">Intégrations</Typography>
                <Grid container spacing={2} columns={{xs: 1, sm: 3}}>
                    <Grid item xs={1}>
                        <TextField
                            id='anael_uid'
                            label='Agent ID (Anael)'
                            {...register('anael_uid')}
                            autoComplete='off'
                            error={errors?.anael_uid ? true : false}
                            helperText={errors?.anael_uid ? errors.anael_uid.message as string : null}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: '1rem'}} />
            <Box sx={{ mt: '1rem' }}>
            <Typography variant="h5">Agences</Typography>
                <Grid container spacing={3} columns={{xs: 1, sm: 3}}>
                    {isAgenciesLoading ? (<CircularProgress size={10} />) : isAgenciesError ? (<Typography color={"red"}>Une erreur est survenue.</Typography>) : fields.map((item, index) => (
                        <Grid item xs={1} key={item.id} sx={{position: 'relative'}}>
                            <FieldAutocompleteList
                                key={item.id}
                                control={control}
                                options={agencyOptions}
                                fieldName={`agency_ids[${index}]`}
                                labelId={`agency_ids-${index}`}
                                label='Agence affiliée'
                            />
                            <IconButton onClick={() => remove(index)} color='warning' sx={{position: 'absolute', top: 15, right: -20, zIndex: 100}}><HighlightOff /></IconButton>
                        </Grid>
                    ))}
                    <GridBreak />
                    <Grid item xs={2}><Button variant='outlined' size='small' color='info' sx={{mt: '1rem'}} onClick={() => append(undefined)} disabled={isAgenciesLoading}><AddCircleOutlineOutlined sx={{mr: '8px'}}/>Ajouter</Button></Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export { Form };